/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import Paper from "@mui/material/Paper";
import FormFader from "../Fader/FormFader";
import Grid from "@mui/material/Grid";
import FormSubmit from "../FormSubmit/FormSubmit";
import { useForm } from "../../../../provider/Form/FormProvider";
import ValidatorTextField from "../../../FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "../FormLegend/FormLegend";
import FormSubmitProps from "../../../../types/FormSubmitProps";
import wizardStyle from "../../wizardStyle";
import { Alert, Box } from "@mui/material";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import WaitingForAiResponse from "../WaitingForAiResponse/WaitingForAiResponse";
import { useWaitForAsyncFields } from "../../../../hooks/useWairForAsyncFields";

type RefinementProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

const REQUIRED_FIELDS = ["aiInitialAssessment", "aiRefinementQuestions"] as const;
type RequiredField = typeof REQUIRED_FIELDS[number];

export default function Refinement({ submitProps, product }: RefinementProps) {
  const { handleSubmit } = useForm();

  const { isWaiting: isWaitingForAiResponse } = useWaitForAsyncFields<AbstractCase, RequiredField>({
    product,
    requiredFields: REQUIRED_FIELDS,
  });

  if (isWaitingForAiResponse) {
    return (
      <WaitingForAiResponse
        mobileHeadline={"Ihr Anliegen"}
        headline={"Wir haben Ihr Anliegen wie folgt verstanden:"}
        text={"Ihr Anliegen wird gerade analysiert und zusammengefasst. Dies kann bis zu einer Minute dauern. Bitte laden Sie die Seite nicht neu."}
      />
    );
  }

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Anliegen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Wir haben Ihr Anliegen wie folgt verstanden:</strong>
              </FormLegend>
            </Grid>
            {!isWaitingForAiResponse && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>{product.aiInitialAssessment}</FormLegend>
                </Grid>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <Alert
                    severity="info"
                    sx={{
                      mt: "1rem",
                      mb: "2rem",
                      color: (theme) => theme.palette.info.dark,
                    }}
                  >
                    <Typography variant="h6">Bitte lassen Sie uns noch folgende Informationen zukommen:</Typography>
                    <Typography dangerouslySetInnerHTML={{ __html: product.aiRefinementQuestions }} />
                  </Alert>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="refinementAnswers"
                    label="Ergänzende Informationen"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    multiline
                    rows={10}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} disabled={isWaitingForAiResponse} />
        </Box>
      </FormFader>
    </Paper>
  );
}
